import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StorableSubject } from 'app/shared/helpers';
import { Client, Site, SiteStorageResponse, SiteType } from 'app/shared/types';
import { CaptureWithTask } from 'app/shared/types/image-capture/capture-with-task';
import { UserInformation } from 'app/shared/types/user/user-information';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { BehaviorSubject, catchError, filter, map, of, switchMap } from 'rxjs';
import { StorageType } from 'shared/enums';
import { SitesService } from 'shared/services';

@Injectable({
  providedIn: 'root',
})
export class ContextService {
  private siteSvc = inject(SitesService);
  private destroyRef = inject(DestroyRef);
  public clientId = new StorableSubject<string | null>(null, 'clientId', StorageType.LOCAL);
  public siteId = new StorableSubject<string | null>(null, 'siteId', StorageType.LOCAL);
  public site = new StorableSubject<Site | null>(null, 'site', StorageType.LOCAL);
  public siteType = new StorableSubject<string | null>(null, 'siteType', StorageType.LOCAL);
  public clients = new StorableSubject<Client[]>([], 'clients');
  public siteTypes = new StorableSubject<SiteType[]>([], 'siteTypes');
  public sites = new StorableSubject<Site[]>([], 'sites');
  public isSuperAdmin = new StorableSubject<boolean>(false, 'isSuperAdmin', StorageType.LOCAL);
  public userInfo = new StorableSubject<UserInformation | null>(null, 'userInfo');
  public featureFlags = new BehaviorSubject<LDFlagSet | null>(null);
  public sitesRefreshTrigger = new BehaviorSubject<void | null>(null);

  private localUserSettings: Record<string, unknown> = {};
  public storageAccess = new StorableSubject<SiteStorageResponse | null>(null, 'storageAccess', StorageType.LOCAL);
  public imageCaptureCache = new StorableSubject<CaptureWithTask | null>(null, 'imageCaptureCache', StorageType.LOCAL);

  public siteWithCapabilities$ = this.site.asObservable().pipe(
    filter((x) => !!x),
    switchMap((selectedSite) =>
      this.siteSvc.getSiteCapabilities(selectedSite.id).result$.pipe(
        map((siteCapabilities) => ({
          ...selectedSite,
          capabilities: siteCapabilities.data,
        })),
        catchError((err: Error) => {
          console.error(err);
          return of(selectedSite);
        }),
      ),
    ),
  );

  constructor() {
    this.userInfo
      .asObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userInfo) => {
        if (userInfo?.id) {
          this.localUserSettings = JSON.parse(localStorage.getItem(userInfo?.id) || '{}');
        }
      });
  }

  public storeItem(key: string, value: unknown): void {
    const userId = this.userInfo.getValue()?.id;
    if (userId) {
      this.localUserSettings[key] = value;
      localStorage.setItem(userId, JSON.stringify(this.localUserSettings));
    }
  }

  public getItem<T>(key: string): T {
    return this.localUserSettings[key] as T;
  }

  public removeItem(key: string): void {
    const userId = this.userInfo.getValue()?.id;
    if (userId) {
      delete this.localUserSettings[key];
      localStorage.setItem(userId, JSON.stringify(this.localUserSettings));
    }
  }

  public updateSiteContext(selectedSite: Site | null): void {
    this.site.next(selectedSite || null);
    this.siteId.next(selectedSite?.id || null);
    this.siteType.next(selectedSite?.siteType || null);
  }

  public isFeatureFlagEnabled(flagName: string): boolean {
    const flags = this.featureFlags.getValue();
    return flags?.[flagName] === true;
  }

  public isSasTokenValid(token: string): boolean {
    try {
      const params = new URLSearchParams(token);
      const expiration = params.get('se');
      const permissions = params.get('sp');

      if (!expiration || !permissions) {
        return false;
      }

      const expirationDate = new Date(expiration);
      const now = new Date();
      if (expirationDate <= now) {
        return false;
      }

      const requiredPermissions = ['r', 'w', 'd', 'l'];
      const hasRequiredPermissions = requiredPermissions.every((perm) => permissions.includes(perm));
      console.log('SAS Token Permissions:', permissions, 'Valid:', hasRequiredPermissions);

      return hasRequiredPermissions;
    } catch (error) {
      console.error('Error validating SAS token:', error);
      return false;
    }
  }

  public clearSasToken(): void {
    this.storageAccess.next(null);
    localStorage.removeItem('storageAccess');
  }

  public clearImageCapture(): void {
    this.imageCaptureCache.next(null);
    localStorage.removeItem('imageCaptureCache');
  }
}
